import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, AlertCircle } from 'lucide-react';

interface ContactFormData {
  fullName: string;
  email: string;
  phoneNumber?: string;
  reason: string;
  message: string;
}

interface ContactFormProps {
  children: React.ReactNode;
  defaultValues?: {
    reason?: string;
    message?: string;
  };
}

export function ContactForm({ children, defaultValues }: ContactFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<ContactFormData>({
    defaultValues: {
      reason: defaultValues?.reason || '',
      message: defaultValues?.message || ''
    }
  });

  const watchReason = watch('reason');

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://muslifin-website.oussama-ad8.workers.dev/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          ...data,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus('success');
        reset();
        
        // Close modal after 3 seconds on success
        setTimeout(() => {
          setIsOpen(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        throw new Error('Submission failed');
      }
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSubmitStatus('idle');
      reset();
    } else if (open && defaultValues) {
      // Set default values when opening the modal
      if (defaultValues.reason) {
        setValue('reason', defaultValues.reason);
      }
      if (defaultValues.message) {
        setValue('message', defaultValues.message);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#004aad]">Contact Us</DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll get back to you shortly.
          </DialogDescription>
        </DialogHeader>

        {submitStatus === 'success' && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Thank you! Your message has been received. We'll be in touch shortly.
            </AlertDescription>
          </Alert>
        )}

        {submitStatus === 'error' && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              Something went wrong. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-gray-700">
              Full Name *
            </Label>
            <Input
              id="fullName"
              {...register('fullName', { 
                required: 'Full name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.fullName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="Enter your full name"
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm">{errors.fullName.message}</p>
            )}
          </div>

          {/* Email Address */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              {...register('email', { 
                required: 'Email address is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.email ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email.message}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="text-gray-700">
              Phone Number
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              {...register('phoneNumber', {
                pattern: {
                  value: /^[\+]?[1-9][\d]{0,15}$/,
                  message: 'Please enter a valid phone number'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.phoneNumber ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="+1 (555) 123-4567"
            />
            {errors.phoneNumber && (
              <p className="text-red-500 text-sm">{errors.phoneNumber.message}</p>
            )}
          </div>

          {/* We Are */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-gray-700">
              We Are... *
            </Label>
            <Select 
              onValueChange={(value: string) => setValue('reason', value)}
              value={watchReason}
            >
              <SelectTrigger 
                className={`
                  focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                  ${errors.reason ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
                `}
              >
                <SelectValue placeholder="Select who you are" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dealership">A Dealership</SelectItem>
                <SelectItem value="investor">An Investor</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="hidden"
              {...register('reason', { required: 'Please select who you are' })}
            />
            {errors.reason && (
              <p className="text-red-500 text-sm">{errors.reason.message}</p>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-700">
              Message *
            </Label>
            <Textarea
              id="message"
              {...register('message', { 
                required: 'Message is required',
                minLength: { value: 10, message: 'Message must be at least 10 characters' }
              })}
              className={`
                min-h-[120px] resize-y focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.message ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="Please provide details about your inquiry..."
            />
            {errors.message && (
              <p className="text-red-500 text-sm">{errors.message.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full bg-[#004aad] hover:bg-[#003a8a] text-white py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}